<script lang="ts" setup>
import { ref, onMounted, onUnmounted } from 'vue'
import RoomieToast from '@lahaus-roomie/roomie/src/components/RoomieToast/index.vue'

import type { Props } from './types'
import { SCREEN } from '@/utils/segment'
import AppChatWindow from '@/components/App/Chat/Window/index.vue'
import AppChatButton from '@/components/App/Chat/Button/index.vue'
import { useAppChatStore } from '@/stores/appChatStore'

const appChatStore = useAppChatStore()

const props = withDefaults(defineProps<Props>(), {
  screen: SCREEN.PDP_INVESTORS
})

const isAppChatButtonVisible = ref(true)
const isChatErrorToastVisible = ref(false)

const handleChatError = () => {
  appChatStore.closeChatWindow()
  isChatErrorToastVisible.value = true

  setTimeout(() => {
    isChatErrorToastVisible.value = false
  }, 5000)
}
const appChatButton = ref(null)

let observer: IntersectionObserver

const handleObservedEntry = (entry: IntersectionObserverEntry) => {
  const nextVisibilityValue = !entry.isIntersecting

  isAppChatButtonVisible.value = nextVisibilityValue
}

const initializeVisibilityObserver = () => {
  if (!props.hidersSelectors) return

  observer = new IntersectionObserver((entries) => {
    entries.forEach(handleObservedEntry)
  }, { threshold: 1 })

  for (const selector of props.hidersSelectors) {
    const element = document.querySelector(selector)
    if (element) observer.observe(element)
  }
}

const disconnectVisibilityObserver = () => {
  if (!props.hidersSelectors || !observer) return
  observer.disconnect()
}

onMounted(() => {
  initializeVisibilityObserver()
})

onUnmounted(() => {
  disconnectVisibilityObserver()
})

const { t } = useI18n()
</script>

<script lang="ts">
export default {
  name: 'AppChat'
}
</script>

<template>
  <section class="app-chat">
    <div class="flex flex-col">
      <Transition
        name="slide-left">
        <AppChatWindow
          v-if="appChatStore.isChatWindowOpen"
          bot-agent-name="Sam"
          bot-agent-image="https://lahaus.imgix.net/static/lh-landing/img-sam-profile.png?auto=compress,enhance,format"
          :screen
          :listing
          :initial-chat-properties="appChatStore.contactFormConfig"
          :show-form="appChatStore.showForm"
          :face-to-face-visit-enabled
          @close-chat="appChatStore.closeChatWindow()"
          @error="handleChatError" />
      </Transition>

      <Transition name="slide-right">
        <AppChatButton
          v-if="isAppChatButtonVisible"
          ref="appChatButton"
          class="self-end"
          :bot-agent-image
          :bot-agent-name
          :listing
          :screen
          :selector-to-trigger-button-chips
          :face-to-face-visit-enabled />
      </Transition>
    </div>

    <RoomieToast
      v-if="isChatErrorToastVisible"
      id="app-chat-error-toast"
      class="app-chat__toast"
      :title="t('samGpt.errorToast.title')"
      :description="t('samGpt.errorToast.description')"
      :is-visible="isChatErrorToastVisible"
      variant="error"
      @toast-closed="isChatErrorToastVisible = false" />
  </section>
</template>

<style lang="scss">
.app-chat {
  &__toast {
    position: fixed;
    top: 50px;
    right: 0;
  }
}
</style>
